/**
 * One trial (ADR-0036; algorithm ADR-0042, the DAQUMA AI Completeness Check):
 * summary cards, sources, the Veeva-vs-Protocol metadata consistency panel,
 * the expected TMF artifacts table (one representative Veeva document per
 * artifact: Existence · vs TMF · vs SOP · vs Protocol · Quality · Score, with
 * All / Present / Missing / Needs attention filters), the documents filed
 * outside the expected set, and a drill-down per artifact. "Run completeness
 * check" (all or one artifact) queues a background run; its progress is live.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Collapse,
  FileButton,
  Group,
  List,
  Modal,
  Paper,
  Select,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconExternalLink,
  IconPlayerPlay,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  ChatModelPicker,
  RunStatus,
  choiceToRequest,
  defaultChoiceWithPlatform,
  useChatModels,
  type ChatModelChoice,
} from "@/features/solutions/shared";
import {
  assignTrialRow,
  listAssignableUsers,
  listTrialAssignments,
  removeTrialSource,
  startTrialCheck,
  uploadTrialSource,
  vaultDocumentUrl,
  type AssignableUserOut,
  type AssignmentOut,
  type ColumnScore,
  type CompletenessRow,
  type MetadataComparison,
  type TrialDetailOut,
} from "@/shared/api/etmfCompleteness";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import {
  COLUMNS,
  RAG_COLOR,
  RAG_LABEL,
  VERDICT_COLOR,
  VERDICT_LABEL,
  formatUtcShort,
  isMissing,
  needsAttention,
  ragOf,
  scoreText,
  type ColumnKey,
} from "./completenessMeta";

interface Props {
  trial: TrialDetailOut;
  greenMin: number;
  amberMin: number;
  onChange: (trial: TrialDetailOut) => void;
  reload: () => Promise<void>;
}

type Filter = "all" | "present" | "missing" | "attention";

function Tile({
  label,
  value,
  color,
  hint,
}: {
  label: string;
  value: string;
  color?: string;
  hint?: string;
}) {
  const body = (
    <Paper withBorder radius="md" p="sm">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text fz={22} fw={700} lh={1.2} c={color}>
        {value}
      </Text>
    </Paper>
  );
  return hint ? <Tooltip label={hint}>{body}</Tooltip> : body;
}

const EMPTY_SCORE: ColumnScore = { verdict: "not_run", explanation: "", misfiled: false };

function colOf(row: CompletenessRow, key: ColumnKey): ColumnScore {
  return row[key] ?? EMPTY_SCORE;
}

const SEVERITY_COLOR: Record<string, string> = { high: "red", medium: "yellow", low: "gray" };
const RESULT_COLOR: Record<string, string> = {
  inconsistent: "red",
  missing_in_veeva: "yellow",
  consistent: "teal",
};
const DIMENSION_LABEL: Record<ColumnKey, string> = {
  existence: "Existence (Veeva + metadata + status)",
  tmf: "Completeness vs TMF definition",
  sop: "Completeness vs SOP",
  protocol: "Consistency vs Protocol",
  quality: "Quality (ALCOA+)",
};

function pct(n: number | null | undefined): string {
  return n != null ? String(n) : "—";
}

export function TrialDetail({ trial, greenMin, amberMin, onChange, reload }: Props) {
  const getToken = useAccessToken();
  const models = useChatModels(true);
  const [choice, setChoice] = useState<ChatModelChoice | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<CompletenessRow | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [showExtra, setShowExtra] = useState(false);
  const [users, setUsers] = useState<AssignableUserOut[]>([]);
  const [assignments, setAssignments] = useState<Record<string, AssignmentOut>>({});

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const token = await getToken();
        const [u, a] = await Promise.all([
          listAssignableUsers(token),
          listTrialAssignments(token, trial.trial_id),
        ]);
        if (!live) return;
        setUsers(u.items);
        setAssignments(Object.fromEntries(a.items.map((x) => [x.artifact_number, x])));
      } catch (err) {
        toastApiError("Could not load reviewers", err);
      }
    })();
    return () => {
      live = false;
    };
  }, [getToken, trial.trial_id]);

  const userOptions = useMemo(
    () => users.map((u) => ({ value: u.user_id, label: u.display_name })),
    [users],
  );

  const assign = async (artifactNumber: string, userId: string | null) => {
    try {
      const res = await assignTrialRow(await getToken(), trial.trial_id, artifactNumber, userId);
      setAssignments(Object.fromEntries(res.items.map((x) => [x.artifact_number, x])));
      toastSuccess(
        userId ? "Assigned" : "Unassigned",
        userId
          ? `${artifactNumber} now appears in ${users.find((u) => u.user_id === userId)?.display_name ?? "the user"}'s Assignments.`
          : `${artifactNumber} is no longer assigned.`,
      );
    } catch (err) {
      toastApiError("Could not assign the row", err);
    }
  };
  const effective =
    choice ?? (models.options ? defaultChoiceWithPlatform(models.options, models.platform) : null);

  const results = trial.results ?? null;
  const summary = results?.summary ?? trial.summary ?? null;
  const rows = useMemo(() => results?.rows ?? [], [results]);
  const metadata = results?.metadata ?? null;
  const extras = results?.unmatched ?? [];
  const running =
    trial.last_run !== null &&
    trial.last_run !== undefined &&
    (trial.last_run.status === "queued" || trial.last_run.status === "running");
  const rag = ragOf(summary?.overall, greenMin, amberMin);

  const shown = useMemo(
    () =>
      rows.filter((r) => {
        if (filter === "present") return !isMissing(r);
        if (filter === "missing") return isMissing(r);
        if (filter === "attention") return needsAttention(r, amberMin);
        return true;
      }),
    [rows, filter, amberMin],
  );

  const upload = async (role: "protocol" | "sop", file: File | null) => {
    if (!file) return;
    try {
      onChange(await uploadTrialSource(await getToken(), trial.trial_id, role, file));
      toastSuccess(
        "Uploaded",
        role === "protocol"
          ? `Protocol "${file.name}" attached. Run the check to use it.`
          : `${file.name} was added to the trial.`,
      );
    } catch (err) {
      toastApiError("Upload failed", err);
    }
  };

  const remove = async (sourceId: string) => {
    const reason = window.prompt("Why remove this document? (recorded in the audit trail)");
    if (!reason?.trim()) return;
    try {
      onChange(await removeTrialSource(await getToken(), trial.trial_id, sourceId, reason));
    } catch (err) {
      toastApiError("Could not remove the document", err);
    }
  };

  const run = async (artifactNumbers: string[] | null) => {
    if (!effective) return;
    setBusy(true);
    try {
      await startTrialCheck(await getToken(), trial.trial_id, {
        artifact_numbers: artifactNumbers,
        ...choiceToRequest(effective),
      });
      await reload();
    } catch (err) {
      toastApiError("Could not start the check", err);
    } finally {
      setBusy(false);
    }
  };

  const onFinished = useCallback(() => void reload(), [reload]);

  const cellColor = (c: ColumnScore) =>
    c.score != null
      ? RAG_COLOR[ragOf(c.score, greenMin, amberMin)]
      : (VERDICT_COLOR[c.verdict] ?? "gray");

  const hasUploadedProtocol = trial.sources.some((s) => s.role === "protocol");
  const protocolLine = hasUploadedProtocol
    ? `Protocol: ${trial.sources.find((s) => s.role === "protocol")?.filename ?? "uploaded"}${
        results?.protocol_source?.startsWith("Uploaded") ? "" : " — takes effect on the next run"
      }`
    : results?.protocol_source
      ? `Protocol: ${results.protocol_source}`
      : "Protocol: from Veeva on the next run (or upload one)";

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <div>
          <Title order={3}>{trial.name}</Title>
          <Text size="sm" c="dimmed">
            Study {trial.study_label} · {trial.connection_name}
            {summary?.documents_in_study
              ? ` · ${summary.documents_in_study} documents in Veeva`
              : ""}{" "}
            · {protocolLine}
          </Text>
          <Text size="xs" c="dimmed">
            Last checked {formatUtcShort(trial.last_checked_at_utc)}
          </Text>
        </div>
        <Badge color={RAG_COLOR[rag]} variant="filled" size="lg">
          {RAG_LABEL[rag]}
        </Badge>
      </Group>

      {trial.last_run?.status === "failed" && (
        <Alert color="red" title="Last run failed">
          {trial.last_run.error ?? "See the run for details, and re-run."}
        </Alert>
      )}

      <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 6 }}>
        <Tile
          label="Overall completeness"
          value={`${pct(summary?.overall)}${summary?.partial ? " (in progress)" : ""}`}
          color={RAG_COLOR[rag]}
          hint="Mean per-artifact score; a missing artifact scores 0"
        />
        <Tile
          label="Expected artifacts present"
          value={
            summary ? `${summary.present} / ${summary.expected}` : `— / ${trial.artifact_count}`
          }
        />
        <Tile
          label="Missing"
          value={pct(summary?.missing)}
          color={summary?.missing ? "red" : undefined}
        />
        <Tile label="Filed outside expected set" value={pct(summary?.unmatched_documents)} />
        <Tile
          label="Check filing"
          value={pct(summary?.misfiled)}
          hint="Content looks like a different artifact"
          color={summary?.misfiled ? "orange" : undefined}
        />
        <Tile label="Avg existence" value={pct(summary?.averages?.existence)} />
        <Tile label="Avg vs TMF" value={pct(summary?.averages?.tmf)} />
        <Tile label="Avg vs SOP" value={pct(summary?.averages?.sop)} />
        <Tile label="Avg vs Protocol" value={pct(summary?.averages?.protocol)} />
        <Tile label="Avg Quality (ALCOA+)" value={pct(summary?.averages?.quality)} />
        <Tile label="Metadata score" value={pct(summary?.metadata_score)} />
        <Tile
          label="Metadata inconsistencies"
          value={String(summary?.open_inconsistencies ?? 0)}
          color={summary?.open_inconsistencies ? "red" : undefined}
        />
      </SimpleGrid>

      <Paper withBorder radius="md" p="md">
        <Stack gap="sm">
          <Group justify="space-between" wrap="wrap">
            <Title order={5}>Protocol and SOP / standards</Title>
            <Group gap="xs">
              <FileButton onChange={(f) => void upload("protocol", f)}>
                {(props) => (
                  <Tooltip label="Upload a protocol (overrides the one filed in Veeva)">
                    <Button
                      {...props}
                      size="xs"
                      variant="light"
                      leftSection={<IconUpload size={14} />}
                    >
                      {trial.has_protocol ? "Replace protocol" : "Upload protocol"}
                    </Button>
                  </Tooltip>
                )}
              </FileButton>
              <FileButton onChange={(f) => void upload("sop", f)}>
                {(props) => (
                  <Button
                    {...props}
                    size="xs"
                    variant="light"
                    leftSection={<IconUpload size={14} />}
                  >
                    Add SOP
                  </Button>
                )}
              </FileButton>
            </Group>
          </Group>
          {trial.sources.length === 0 ? (
            <Text size="sm" c="dimmed">
              No uploads. The Protocol filed in Veeva for this study is used when none is uploaded;
              with no SOP the “vs SOP” column shows N/A.
            </Text>
          ) : (
            <Stack gap={4}>
              {trial.sources.map((s) => (
                <Group key={s.source_id} gap="xs" justify="space-between" wrap="nowrap">
                  <Group gap="xs" wrap="nowrap">
                    <Badge variant="light" color={s.role === "protocol" ? "blue" : "grape"}>
                      {s.role === "protocol" ? "Protocol" : "SOP"}
                    </Badge>
                    <Text size="sm">{s.filename}</Text>
                  </Group>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label={`Remove ${s.filename}`}
                    onClick={() => void remove(s.source_id)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Stack gap="sm">
          <Title order={5}>Run completeness check</Title>
          <ChatModelPicker
            options={models.options}
            error={models.error}
            platform={models.platform}
            value={effective ?? { connectionId: null, deployment: null }}
            onChange={setChoice}
            footnote="This model scores every document and compares the study metadata."
            disabled={running}
          />
          <Group>
            <Button
              leftSection={<IconPlayerPlay size={16} />}
              onClick={() => void run(null)}
              loading={busy}
              disabled={running || !effective?.connectionId}
            >
              Run completeness check
            </Button>
          </Group>
          {trial.last_run && trial.last_run.mine && (
            <RunStatus runId={trial.last_run.run_id} onFinished={onFinished} />
          )}
          {trial.last_run && !trial.last_run.mine && running && (
            <Text size="sm" c="dimmed">
              A check started by someone else is {trial.last_run.status} (
              {trial.last_run.progress_done} / {trial.last_run.progress_total}).
            </Text>
          )}
          {results && summary?.overall == null && !running && (summary?.present ?? 0) > 0 && (
            <Text size="sm" c="dimmed">
              Click Run completeness check to score the documents.
            </Text>
          )}
        </Stack>
      </Paper>

      <MetadataPanel metadata={metadata} running={running} />

      <Paper withBorder radius="md" p="md">
        <Stack gap="sm">
          <Group justify="space-between" wrap="wrap">
            <Title order={5}>Expected TMF artifacts</Title>
            <SegmentedControl
              size="xs"
              value={filter}
              onChange={(v) => setFilter(v as Filter)}
              data={[
                { value: "all", label: "All" },
                { value: "present", label: "Present" },
                { value: "missing", label: "Missing" },
                { value: "attention", label: "Needs attention" },
              ]}
            />
          </Group>
          {rows.length === 0 ? (
            <Text size="sm" c="dimmed">
              No results yet. Run the completeness check to match the vault's documents with the{" "}
              {trial.artifact_count} expected artifacts.
            </Text>
          ) : shown.length === 0 ? (
            <Text size="sm" c="dimmed">
              Nothing matches this filter.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={1180}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Artifact</Table.Th>
                    <Table.Th>Veeva document</Table.Th>
                    {COLUMNS.map((c) => (
                      <Table.Th key={c.key}>
                        <Tooltip label={c.hint}>
                          <span>{c.label}</span>
                        </Tooltip>
                      </Table.Th>
                    ))}
                    <Table.Th>Score</Table.Th>
                    <Table.Th>Reviewer</Table.Th>
                    <Table.Th aria-label="Actions" />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {shown.map((r) => (
                    <Table.Tr
                      key={r.row_id}
                      data-testid="completeness-row"
                      style={{ cursor: "pointer" }}
                      onClick={() => setOpen(r)}
                    >
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {r.artifact_number} {r.artifact_name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {r.zone_name}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {isMissing(r) ? (
                          <Badge color="red" variant="light">
                            missing
                          </Badge>
                        ) : (
                          <Stack gap={2}>
                            <Group gap={4} wrap="nowrap">
                              <Text size="sm">{r.document_number ?? r.document_name}</Text>
                              {(r.matched_count ?? 0) > 1 && (
                                <Badge size="xs" variant="outline">
                                  +{(r.matched_count ?? 1) - 1} more
                                </Badge>
                              )}
                              {r.misfiled && (
                                <Tooltip label="The content looks like a different artifact">
                                  <Badge size="xs" color="orange">
                                    check filing
                                  </Badge>
                                </Tooltip>
                              )}
                            </Group>
                            {r.status && (
                              <Text size="xs" c="dimmed">
                                {r.status}
                              </Text>
                            )}
                          </Stack>
                        )}
                      </Table.Td>
                      {COLUMNS.map((c) => {
                        const score = colOf(r, c.key);
                        return (
                          <Table.Td key={c.key}>
                            <Badge variant="light" color={cellColor(score)}>
                              {scoreText(score)}
                            </Badge>
                          </Table.Td>
                        );
                      })}
                      <Table.Td>
                        <Badge
                          variant="filled"
                          color={
                            r.overall != null
                              ? RAG_COLOR[ragOf(r.overall, greenMin, amberMin)]
                              : "gray"
                          }
                        >
                          {pct(r.overall)}
                        </Badge>
                      </Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()} miw={190}>
                        <Select
                          size="xs"
                          placeholder="Assign…"
                          searchable
                          clearable
                          data={userOptions}
                          value={assignments[r.artifact_number]?.assignee_user_id ?? null}
                          onChange={(v) => void assign(r.artifact_number, v)}
                          aria-label={`Reviewer for ${r.artifact_number}`}
                        />
                        {assignments[r.artifact_number]?.status === "reviewed" && (
                          <Badge size="xs" color="teal" variant="light" mt={4}>
                            reviewed
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Tooltip label="Re-check this artifact">
                          <ActionIcon
                            variant="subtle"
                            aria-label={`Re-check ${r.artifact_number}`}
                            disabled={running || busy || !effective?.connectionId}
                            onClick={() => void run([r.artifact_number])}
                          >
                            <IconPlayerPlay size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
          {(results?.notes ?? []).map((n) => (
            <Text key={n} size="xs" c="dimmed">
              {n}
            </Text>
          ))}
        </Stack>
      </Paper>

      {extras.length > 0 && (
        <Paper withBorder radius="md" p="md">
          <UnstyledButton onClick={() => setShowExtra((v) => !v)}>
            <Group gap="xs">
              {showExtra ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
              <Title order={5}>
                Documents filed in Veeva outside the expected set ({extras.length})
              </Title>
            </Group>
          </UnstyledButton>
          <Collapse in={showExtra}>
            <Table.ScrollContainer minWidth={640} mt="sm">
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Document</Table.Th>
                    <Table.Th>Classification</Table.Th>
                    <Table.Th>Catalogue artifact</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {extras.map((d) => {
                    const url = vaultDocumentUrl(trial.vault_url, d.document_id);
                    return (
                      <Table.Tr key={d.document_id}>
                        <Table.Td>
                          {url ? (
                            <Anchor href={url} target="_blank" rel="noreferrer" size="sm">
                              {d.number ?? d.document_id} {d.name}
                            </Anchor>
                          ) : (
                            <Text size="sm">
                              {d.number ?? d.document_id} {d.name}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>{d.type ?? "—"}</Table.Td>
                        <Table.Td>
                          {d.catalogue_artifact ?? (
                            <Text size="sm" c="dimmed">
                              not in catalogue
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>{d.status ?? "—"}</Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Collapse>
        </Paper>
      )}

      <Modal
        opened={open !== null}
        onClose={() => setOpen(null)}
        size="95%"
        centered
        withCloseButton
        title={
          open ? (
            <Text fw={700} size="lg">
              {open.artifact_number} {open.artifact_name}
            </Text>
          ) : null
        }
      >
        {open && (
          <ArtifactDetail row={open} vaultUrlOf={(id) => vaultDocumentUrl(trial.vault_url, id)} />
        )}
      </Modal>
    </Stack>
  );
}

function MetadataPanel({
  metadata,
  running,
}: {
  metadata: TrialDetailOut["results"] extends infer R
    ? R extends { metadata?: infer M }
      ? M | null
      : null
    : null;
  running: boolean;
}) {
  const comparisons: MetadataComparison[] = metadata?.comparisons ?? [];
  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap="sm">
        <Title order={5}>Metadata consistency — Veeva study record vs Protocol</Title>
        {running && (!metadata || metadata.status === "not_run") ? (
          <Text size="sm" c="dimmed">
            Comparing…
          </Text>
        ) : !metadata || metadata.status === "not_run" ? (
          <Text size="sm" c="dimmed">
            Not checked yet. Run the completeness check.
          </Text>
        ) : metadata.status === "failed" ? (
          <Alert color="red" title="The metadata check failed">
            {metadata.explanation}
          </Alert>
        ) : comparisons.length === 0 ? (
          <Text size="sm">{metadata.explanation}</Text>
        ) : (
          <>
            <Text size="sm">
              {metadata.explanation}
              {metadata.protocol_source ? ` (protocol: ${metadata.protocol_source})` : ""}
            </Text>
            <Table.ScrollContainer minWidth={760}>
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Field</Table.Th>
                    <Table.Th>Veeva</Table.Th>
                    <Table.Th>Protocol</Table.Th>
                    <Table.Th>Result</Table.Th>
                    <Table.Th>Severity</Table.Th>
                    <Table.Th>Details</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {comparisons.map((c) => (
                    <Table.Tr key={`${c.fact}-${c.field}`}>
                      <Table.Td>
                        <Text size="sm">{c.label}</Text>
                        <Text size="xs" c="dimmed">
                          {c.field}
                        </Text>
                      </Table.Td>
                      <Table.Td>{c.veeva_value || "(empty)"}</Table.Td>
                      <Table.Td>{c.protocol_value || "—"}</Table.Td>
                      <Table.Td>
                        <Badge color={RESULT_COLOR[c.status]} variant="light">
                          {c.status.replaceAll("_", " ")}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {c.status !== "consistent" && (
                          <Badge color={SEVERITY_COLOR[c.severity ?? "medium"]} variant="light">
                            {c.severity}
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{c.explanation}</Text>
                        {c.source && (
                          <Text size="xs" c="dimmed">
                            {c.source}
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </>
        )}
      </Stack>
    </Paper>
  );
}

function ScoreDetails({ score }: { score: ColumnScore }) {
  return (
    <Stack gap={2}>
      {score.explanation && <Text size="sm">{score.explanation}</Text>}
      {(score.missing_elements ?? []).length > 0 && (
        <>
          <Text size="xs" fw={600}>
            Missing:
          </Text>
          <List size="xs">
            {(score.missing_elements ?? []).map((m) => (
              <List.Item key={m}>{m}</List.Item>
            ))}
          </List>
        </>
      )}
      {(score.inconsistencies ?? []).length > 0 && (
        <>
          <Text size="xs" fw={600}>
            Inconsistencies:
          </Text>
          <List size="xs">
            {(score.inconsistencies ?? []).map((m) => (
              <List.Item key={m}>{m}</List.Item>
            ))}
          </List>
        </>
      )}
      {score.misfiled && (
        <Badge color="orange" size="sm">
          Content looks like a different artifact — check filing
        </Badge>
      )}
    </Stack>
  );
}

export function ArtifactDetail({
  row,
  vaultUrlOf,
}: {
  row: CompletenessRow;
  vaultUrlOf: (documentId: string) => string | null;
}) {
  const url = row.document_id ? vaultUrlOf(row.document_id) : null;
  const docs = row.matched_docs ?? [];
  return (
    <Stack gap="md">
      {isMissing(row) ? (
        <Badge color="red" variant="filled">
          Missing in Veeva
        </Badge>
      ) : (
        <Stack gap={4}>
          <Group gap="xs">
            <Text size="sm" fw={600}>
              Veeva document:
            </Text>
            {url ? (
              <Anchor href={url} target="_blank" rel="noreferrer" size="sm">
                <Group gap={4}>
                  {row.document_number ?? row.document_id} {row.document_name}
                  <IconExternalLink size={14} />
                </Group>
              </Anchor>
            ) : (
              <Text size="sm">{row.document_name}</Text>
            )}
          </Group>
          {row.classification && <Text size="sm">Classification: {row.classification}</Text>}
          {row.status && <Text size="sm">Status: {row.status}</Text>}
          {row.document_version && <Text size="sm">Version: {row.document_version}</Text>}
        </Stack>
      )}
      <Text size="xs" c="dimmed">
        {row.zone_name} › {row.section_name}
      </Text>

      <div>
        <Title order={6} mb={4}>
          Completeness
        </Title>
        <Table withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={230} style={{ whiteSpace: "nowrap" }}>
                Dimension
              </Table.Th>
              <Table.Th w={80} style={{ whiteSpace: "nowrap" }}>
                Score
              </Table.Th>
              <Table.Th w={130} style={{ whiteSpace: "nowrap" }}>
                Verdict
              </Table.Th>
              <Table.Th>Details</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {COLUMNS.map((c) => {
              const s = colOf(row, c.key);
              return (
                <Table.Tr key={c.key}>
                  <Table.Td>
                    <Text size="sm">{DIMENSION_LABEL[c.key]}</Text>
                  </Table.Td>
                  <Table.Td style={{ whiteSpace: "nowrap" }}>{s.score ?? "—"}</Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={VERDICT_COLOR[s.verdict] ?? "gray"}>
                      {VERDICT_LABEL[s.verdict] ?? s.verdict}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <ScoreDetails score={s} />
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </div>

      {docs.length > 0 && (
        <div>
          <Title order={6} mb={4}>
            All documents filed under this artifact ({row.matched_count ?? docs.length})
          </Title>
          <List size="sm">
            {docs.map((d) => {
              const u = vaultUrlOf(d.document_id);
              const label = `${d.number ?? d.document_id} ${d.name}${d.status ? ` · ${d.status}` : ""}`;
              return (
                <List.Item key={d.document_id}>
                  {u ? (
                    <Anchor href={u} target="_blank" rel="noreferrer" size="sm">
                      {label}
                    </Anchor>
                  ) : (
                    label
                  )}
                </List.Item>
              );
            })}
          </List>
          <Text size="xs" c="dimmed">
            The evaluated document is the most final / most recent one.
          </Text>
        </div>
      )}

      {(row.notes ?? []).map((n) => (
        <Text key={n} size="xs" c="dimmed">
          {n}
        </Text>
      ))}
      <Text size="xs" c="dimmed">
        AI-generated decision support — requires human review.
      </Text>
    </Stack>
  );
}
